import { Typography } from '@mui/material';
import { compact, find, groupBy, map, maxBy, minBy, range } from 'lodash';
import { ReactNode } from 'react';

import {
    DeviceAlert,
    GetDeviceAlertConfigurationOptionsResponse,
    SensorAlertTypes,
    TriggerType,
} from '@/api/customSensorAlerts/types';
import { SensorType, sensorIncludesType } from '@/api/device';
import { DeviceSensorConfiguration } from '@/api/device/types';
import { Device } from '@/api/devices';
import { sensorToColor } from '@/charts/common';
import { convert } from '@/common/convert';
import { ColorIcon } from '@/components/data/ColorIcon';
import { DatumContext, DatumUnit, QuantityType, normalizeQuantityType, unitInContext } from '@/components/data/Datum';
import { boldWeight } from '@/theme';
import { sensorDisplayName } from '@/utils/sensorDataHelpers';

const alertsDisplayNameSortOrder = [
    'Significant wave height',
    'Peak wave period',
    'Mean wave period',
    'Wind speed',
    'Barometric pressure',
    'Surface temperature',
    'Subsurface temperature',
    'Dissolved oxygen',
    'Currents speed',
    'Turbidity',
    'Water pressure (depth)',
    'Salinity',
];
const orderMap = new Map(alertsDisplayNameSortOrder.map((value, index) => [value, index]));
const sortByDisplayName = (a: string, b: string) => {
    const aOrder = orderMap.get(a) ?? Infinity;
    const bOrder = orderMap.get(b) ?? Infinity;

    if (aOrder < bOrder) {
        return -1;
    }
    if (aOrder > bOrder) {
        return 1;
    }
    return 0;
};

const ALERT_SENSOR_TYPES: Record<SensorAlertTypes, SensorType | 'spotter'> = {
    spotter_threshold_significantwaveheight: 'spotter',
    spotter_threshold_seasurfacetemperature: 'spotter',
    spotter_threshold_barometricpressure: 'spotter',
    spotter_threshold_windspeed: 'spotter',
    spotter_threshold_meanwaveperiod: 'spotter',
    spotter_threshold_peakwaveperiod: 'spotter',
    smartmooring_threshold_currents_speed: 'currents',
    smartmooring_threshold_dissolved_oxygen_concentration: 'dissolvedOxygen',
    smartmooring_threshold_dissolved_oxygen_saturation: 'dissolvedOxygen',
    smartmooring_threshold_temperature: 'waterTemperature',
    smartmooring_threshold_turbidity: 'turbidity',
    smartmooring_threshold_water_pressure: 'pressure',
    smartmooring_threshold_broadband_spl: 'hydrophone',
    smartmooring_threshold_salinity: 'salinity',
} as const;

const sortTriggerTypesByDisplayName = (a: TriggerType, b: TriggerType) => {
    const displayNameA = a.display_info.name;
    const displayNameB = b.display_info.name;

    return sortByDisplayName(displayNameA, displayNameB);
};

export const sortDeviceAlerts = (a: DeviceAlert, b: DeviceAlert) => {
    const displayNameA = a.trigger_details.displayName;
    const displayNameB = b.trigger_details.displayName;
    const displayNameOrder = sortByDisplayName(displayNameA, displayNameB);
    if (displayNameOrder) return displayNameOrder;

    const sensorPositionA = a.trigger_details.sensorPosition ?? -1; // -1 for no sensor position
    const sensorPositionB = b.trigger_details.sensorPosition ?? -1; // -1 for no sensor position
    return sensorPositionA - sensorPositionB;
};

const OPERATOR_ORDER = ['<', '<=', '>=', '>'];

export const composeAlertOperatorOptions = (options?: GetDeviceAlertConfigurationOptionsResponse['data']) => {
    if (!options) {
        return [];
    }

    return options.operators
        .toSorted((a, b) => {
            // feels like overkill, but want to be able to theoretically support other operators if wavefleet ever returns one
            return OPERATOR_ORDER.indexOf(a) - OPERATOR_ORDER.indexOf(b);
        })
        .map((operator) => ({
            value: operator,
            label: operator,
        }));
};

export const composeAlertTypeOptions = (
    options?: GetDeviceAlertConfigurationOptionsResponse['data'],
    device?: Device
) => {
    if (!options || !device) {
        return [];
    }
    return options.triggerTypes.toSorted(sortTriggerTypesByDisplayName).map((triggerType) => ({
        value: triggerType.trigger_type,
        label: triggerType.display_info.name,
    }));
};

export const composeSensorPositionOptions = (
    alertType: SensorAlertTypes,
    maxSensorPosition: number,
    sensorConfigs?: DeviceSensorConfiguration[]
): { value: string; label: ReactNode }[] => {
    const sensorType = ALERT_SENSOR_TYPES[alertType];
    if (sensorType === 'spotter') {
        return [];
    }
    return range(1, maxSensorPosition + 1).map((position) => {
        const sensorConfig = find(sensorConfigs, { position });
        const hasSensor = sensorConfig && sensorIncludesType(sensorConfig, sensorType);
        const label = (
            <span>
                {position}: {sensorConfig ? sensorDisplayName(sensorConfig) : <em>No sensor connected</em>}
            </span>
        );
        const menuItem = (
            <Typography
                variant="inherit"
                sx={{
                    fontWeight: hasSensor ? boldWeight : undefined,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <ColorIcon color={sensorToColor(position).line} />
                {label}
            </Typography>
        );
        return {
            value: position.toString(),
            label,
            menuItem,
        };
    });
};

export type AlertComparisonOperators = '<' | '<=' | '>' | '>=';

const PRETTY_OPERATORS: Record<string, string> = { '>=': '≥ ', '<=': '≤ ' };
export const prettyOperator = (operator: string) => PRETTY_OPERATORS[operator] ?? operator;

export const isAlertThresholdBreached = (
    currentValue: number,
    operator: AlertComparisonOperators,
    alertThreshold: number
) => {
    switch (operator) {
        case '>':
            return currentValue > alertThreshold;
        case '>=':
            return currentValue >= alertThreshold;
        case '<':
            return currentValue < alertThreshold;
        case '<=':
            return currentValue <= alertThreshold;
        default:
            return false;
    }
};

export type BreachedSensorAlert = {
    id: number;
    type: SensorAlertTypes;
    logicalOperator: AlertComparisonOperators;
    convertedAlertValue: number;
    convertedAlertUnits: DatumUnit;
    unitType: QuantityType;
    displayName: string;
};

export const isGreater = (operator: AlertComparisonOperators) => operator === '>' || operator === '>=';

export const composeMostSevereBreachedAlerts = (breachedAlerts: BreachedSensorAlert[]): BreachedSensorAlert[] => {
    const alertsByOperation = groupBy(breachedAlerts, (alert) => {
        return isGreater(alert.logicalOperator) ? 'greater' : 'lesser';
    });
    return compact(
        map(alertsByOperation, (alerts, operator) =>
            operator === 'greater' ? maxBy(alerts, 'convertedAlertValue') : minBy(alerts, 'convertedAlertValue')
        )
    );
};

export const triggerQuantityType = ({
    trigger_type,
    unitType,
}: Pick<DeviceAlert['trigger_details'], 'trigger_type' | 'unitType'>): QuantityType => {
    if (unitType === 'speed' && trigger_type.includes('currents')) {
        return 'currentSpeed';
    }
    return normalizeQuantityType(unitType);
};

export const applicableDeviceAlerts = (
    alerts: DeviceAlert[],
    triggerType: SensorAlertTypes,
    sensorPosition?: number
): DeviceAlert[] => {
    return alerts.filter((alert) => {
        if (!alert.enabled || alert.trigger_type !== triggerType) {
            return false;
        }
        if (sensorPosition === undefined) {
            return true;
        }
        return alert.trigger_details.sensorPosition === sensorPosition;
    });
};

export const convertAlertThreshold = (
    alert: DeviceAlert,
    targetUnit: DatumUnit,
    context: DatumContext = {}
): { convertedValue: number; unitType: QuantityType } => {
    const unitType = triggerQuantityType(alert.trigger_details);
    const originalAlertUnits = unitInContext(unitType, alert.trigger_details.units, context);
    const originalAlertValue = Number(alert.trigger_details.value);
    const convertedValue = originalAlertUnits
        ? convert(originalAlertValue).from(originalAlertUnits).to(targetUnit)
        : originalAlertValue;
    return { convertedValue, unitType };
};
